import React from 'react';

import { SizeMe, SizeMeProps } from 'react-sizeme';
import { Layouts, Responsive as ResponsiveReactGridLayout } from 'react-grid-layout';
import { Box, useTheme } from '@material-ui/core';

import { WidgetItem } from '../../types';

interface Props {
  widgets: WidgetItem[];
  getWidget: (item: WidgetItem) =>
    | {
        component: (props: any) => JSX.Element;
        props: any;
      }
    | undefined;
  onWidgetStatusChange: Function;
  onLayoutChange: any;
  layouts: any;
  isWidgetsClosable?: boolean;
  /**
   * Called with a widget's system_name when its own request answers 403. Injected
   * into every widget so the dashboard, not each widget, decides what to do about
   * it — see useWidgetErrorHandler.
   */
  onWidgetForbidden?: (systemName: string) => void;
  /**
   * Minimum widget width in px, keyed by system_name. Grid columns stretch with the
   * screen, so a fixed `minW` can't guarantee the content fits — this is converted
   * into `minW` columns for the current grid width instead.
   */
  widgetMinWidths?: Record<string, number>;
}

const margins: [number, number] = [24, 24];
const cols = { lg: 8, md: 4, sm: 2, xs: 1, xxs: 1 };

export const getInitialLayouts = (items: WidgetItem[]): any => ({
  lg: items.map(item => item.layout),
  md: items.map(item => item.layout),
  sm: items.map(item => item.layout),
  xxs: items.map(item => item.layout),
  xs: items.map(item => item.layout),
});

// Keep w/minW/maxW coherent and within the breakpoint's column count — otherwise
// react-grid-layout warns "minWidth larger than item width/maxWidth" (it clamps w to the
// available columns on small breakpoints but leaves minW untouched).
const sanitizeLayoutItem = (item: any, maxCols?: number) => {
  if (!item) return item;
  const cap = (value?: number) => (maxCols && value ? Math.min(value, maxCols) : value);

  const maxW = cap(item.maxW || undefined);
  let minW = item.minW ? (maxW ? Math.min(item.minW, maxW) : item.minW) : undefined;
  minW = cap(minW);

  let w = cap(item.w) ?? item.w;
  if (minW && w < minW) w = minW;
  if (maxW && w > maxW) w = maxW;

  return {
    ...item,
    minH: item.minH || undefined,
    minW,
    maxH: item.maxH || undefined,
    maxW,
    w,
  };
};

// Smallest column span whose rendered width (react-grid-layout: w * colWidth +
// (w - 1) * margin, with containerPadding = margin) is at least `minWidth` px.
const getMinColsForWidth = (minWidth: number, gridWidth: number, colCount: number) => {
  const [marginX] = margins;
  const colWidth = (gridWidth - marginX * (colCount + 1)) / colCount;
  if (colWidth <= 0) return colCount;
  return Math.ceil((minWidth + marginX) / (colWidth + marginX));
};

// `isResizable` and the px min width are widget config, not user state: layouts
// saved while a widget's default had `isResizable: false` keep hiding the resize
// handle forever (every onLayoutChange writes the stale flag back). Always take
// them from the widget's canonical layout, never from the stored one.
const formatLayouts = (
  layouts: any,
  widgets: WidgetItem[],
  gridWidth: number,
  widgetMinWidths?: Record<string, number>
) => {
  if (!layouts) {
    return undefined;
  }

  const canonicalByWidget = new Map<string, { isResizable?: boolean; minW?: number; minWidth?: number }>();
  widgets.forEach(widget => {
    if (!widget.layout?.i) return;
    canonicalByWidget.set(widget.layout.i, {
      isResizable: widget.layout.isResizable,
      minW: widget.layout.minW,
      minWidth: widgetMinWidths?.[widget.system_name],
    });
  });

  let formattedLayout: Layouts = {};
  Object.keys(layouts)?.forEach(key => {
    const maxCols = cols[key as keyof typeof cols];
    formattedLayout[key] = layouts[key]
      ? layouts[key]?.map((item: any) => {
          const canonical = canonicalByWidget.get(item.i);
          const pxMinW =
            canonical?.minWidth && gridWidth
              ? getMinColsForWidth(canonical.minWidth, gridWidth, maxCols)
              : 0;
          const minW = pxMinW ? Math.max(canonical?.minW || 0, pxMinW) : item.minW;
          return sanitizeLayoutItem({ ...item, minW, isResizable: canonical?.isResizable }, maxCols);
        })
      : [];
  });
  return formattedLayout;
};

type DashboardGridProps = Omit<Props, 'getWidget' | 'onWidgetStatusChange' | 'isWidgetsClosable'> & {
  width: number;
  children: React.ReactNode;
};

const DashboardGrid: React.FC<DashboardGridProps> = ({
  width,
  layouts,
  widgets,
  widgetMinWidths,
  onLayoutChange,
  children,
}) => {
  const theme = useTheme();
  const { lg, md, xs, sm } = theme.breakpoints.values;
  const formattedLayouts = React.useMemo(
    () => formatLayouts(layouts ?? getInitialLayouts(widgets), widgets, width, widgetMinWidths),
    [layouts, widgets, width, widgetMinWidths]
  );

  return (
    <ResponsiveReactGridLayout
      className="layout"
      layouts={formattedLayouts}
      isBounded={true}
      margin={margins}
      breakpoints={{ lg, md, sm, xs, xxs: xs }}
      cols={cols}
      rowHeight={40}
      useCSSTransforms={true}
      draggableHandle=".draggable-widget-header"
      onLayoutChange={onLayoutChange}
      width={width}
    >
      {children}
    </ResponsiveReactGridLayout>
  );
};

export const AppDashboard: React.FC<Props> = ({
  widgets,
  layouts,
  getWidget,
  onWidgetStatusChange,
  onLayoutChange,
  isWidgetsClosable = true,
  onWidgetForbidden,
  widgetMinWidths,
}) => {
  const children = React.useMemo(() => {
    return widgets
      ?.filter(item => item.is_active)
      ?.map(item => {
        const { component, props } = getWidget(item) ?? {};
        return component ? (
          <Box
            key={item.system_name}
            className="widget"
            overflow="auto"
            data-grid={sanitizeLayoutItem(item.layout)}
          >
            {React.createElement(component, {
              closable: isWidgetsClosable,
              onRemoveItem: onWidgetStatusChange,
              system_name: item.system_name,
              name: item.name,
              isResizable: item.layout?.isResizable,
              title_color: item.title_color,
              onForbidden: onWidgetForbidden ? () => onWidgetForbidden(item.system_name) : undefined,
              ...props,
            })}
          </Box>
        ) : null;
      });
  }, [widgets, getWidget, onWidgetForbidden]);

  return (
    <SizeMe>
      {(props: SizeMeProps) => (
        <DashboardGrid
          width={props.size.width || 0}
          layouts={layouts}
          widgets={widgets}
          widgetMinWidths={widgetMinWidths}
          onLayoutChange={onLayoutChange}>
          {children}
        </DashboardGrid>
      )}
    </SizeMe>
  );
};
